import {
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { createPortal } from "react-dom";

import { styled } from "@linaria/react";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import { OVERLAY_Z_INDEX_BASE, OVERLAY_Z_INDEX_STEP, OverlayLayer } from "./constants";

export interface OverlayEntry {
  id: string;
  onDismiss: () => void;
  layer: OverlayLayer;
  dismissOnOverlayClick: boolean;
  zIndex: number;
}

export interface OverlayRegisterOptions {
  layer?: OverlayLayer;
  dismissOnOverlayClick?: boolean;
}

export interface OverlayInfo {
  zIndex: number;
  isTopmost: boolean;
}

export interface OverlayContextValue {
  register: (id: string, onDismiss: () => void, options?: OverlayRegisterOptions) => void;
  unregister: (id: string) => void;
  overlays: OverlayEntry[];
}

export const OverlayContext = createContext<OverlayContextValue | null>(null);

const SharedOverlay = withTheme(styled.div<PropsWithTheme<{ isVisible: boolean; $zIndex: number }>>`
  position: fixed;
  inset: 0;

  background-color: ${({ theme }) => theme.color.opacity.bw48};
  backdrop-filter: ${({ isVisible }) => (isVisible ? "blur(4px)" : "blur(0px)")};

  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  visibility: ${({ isVisible }) => (isVisible ? "visible" : "hidden")};

  transition: ${({ isVisible }) =>
    isVisible ? "backdrop-filter 0.2s ease-out, opacity 0.2s ease-out" : "none"};

  z-index: ${({ $zIndex }) => $zIndex};

  pointer-events: ${({ isVisible }) => (isVisible ? "auto" : "none")};
`);

export const OverlayProvider = ({ children }: PropsWithChildren) => {
  const [overlays, setOverlays] = useState<OverlayEntry[]>([]);
  // Counter to assign incrementing z-indices - ensures new overlays always appear on top
  const zIndexCounterRef = useRef(0);

  const register = useCallback(
    (id: string, onDismiss: () => void, options?: OverlayRegisterOptions) => {
      const layer = options?.layer ?? OverlayLayer.DRAWER;
      const dismissOnOverlayClick = options?.dismissOnOverlayClick ?? true;

      setOverlays((prev) => {
        const existingIndex = prev.findIndex((entry) => entry.id === id);

        if (existingIndex === -1) {
          // New overlay - assign next z-index
          const zIndex = OVERLAY_Z_INDEX_BASE + zIndexCounterRef.current * OVERLAY_Z_INDEX_STEP;
          zIndexCounterRef.current += 1;
          return [...prev, { id, onDismiss, layer, dismissOnOverlayClick, zIndex }];
        }

        // Existing overlay - keep same z-index, just update callbacks
        const next = [...prev];
        next[existingIndex] = { ...prev[existingIndex], onDismiss, layer, dismissOnOverlayClick };
        return next;
      });
    },
    [],
  );

  const unregister = useCallback((id: string) => {
    setOverlays((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  // The topmost overlay is the one with the highest z-index
  const topmost = useMemo(() => {
    if (overlays.length === 0) return null;
    return overlays.reduce((top, entry) => (entry.zIndex > top.zIndex ? entry : top), overlays[0]);
  }, [overlays]);

  const handleOverlayClick = useCallback(() => {
    if (!topmost || !topmost.dismissOnOverlayClick) {
      return;
    }

    topmost.onDismiss();
  }, [topmost]);

  const contextValue = useMemo(
    () => ({
      register,
      unregister,
      overlays,
    }),
    [register, unregister, overlays],
  );

  const isVisible = !!topmost;
  // Backdrop sits directly behind the topmost overlay
  const backdropZIndex = topmost ? topmost.zIndex - 1 : OVERLAY_Z_INDEX_BASE - 1;

  return (
    <OverlayContext.Provider value={contextValue}>
      {children}
      {createPortal(
        <SharedOverlay
          isVisible={isVisible}
          $zIndex={backdropZIndex}
          onClick={handleOverlayClick}
        />,
        document.body,
      )}
    </OverlayContext.Provider>
  );
};

export default OverlayProvider;

export {
  getOverlayZIndex,
  OVERLAY_LAYER_PRIORITY,
  OVERLAY_Z_INDEX,
  OVERLAY_Z_INDEX_BASE,
  OVERLAY_Z_INDEX_STEP,
  OverlayLayer,
} from "./constants";
